import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Link, useLocation, useParams, useNavigate } from 'react-router-dom';
import { 
  Menu, X, ChevronDown, Calendar, MapPin, Clock, FileText, Download, 
  Mail, Phone, Map, Shield, Users, Info, ArrowRight, CheckCircle, AlertTriangle, Image as ImageIcon, Copy, Images, Eye, ZoomIn
} from 'lucide-react';
import ScrollToTop from './components/ScrollToTop';
import { defaultDocuments, defaultEvents, defaultBoardMembers, defaultGallery } from './data';
import NewsletterRecipients, { saveSubscriber } from './pages/NewsletterRecipients';
import EventRegistrations, { saveEventRegistration } from './pages/EventRegistrations';
import GalleryModal from './components/GalleryModal';

// --- HELPER TO FORMAT DATE ---
function formatDate(dateStr) {
  if (!dateStr) return '';
  const options = { year: 'numeric', month: 'long', day: 'numeric' };
  return new Date(dateStr).toLocaleDateString('da-DK', options);
}

// --- HELPER TO STRIP HTML TAGS ---
function stripHtml(html) {
  if (typeof html !== 'string') return '';
  return html.replace(/<\/?[^>]+(>|$)/g, "").trim();
}

// --- HELPER FOR CDN IMAGE RESIZING ---
function getImageUrl(img, width = 800) {
  if (!img) return "";
  const url = Array.isArray(img) ? img[0] : img;
  if (!url) return "";
  if (url.includes('assets.auxodev.dk') && !url.includes('/cdn-cgi/image/')) {
    return url.replace('https://assets.auxodev.dk/', `https://assets.auxodev.dk/cdn-cgi/image/width=${width},quality=80,format=auto/`);
  }
  return url;
}

// --- DATA NORMALIZERS FOR DANISH CMS COLLECTIONS ---
function normalizeBegivenhed(e) {
  if (!e) return e;
  return {
    ...e,
    title: e.titel || e.title || '',
    date: e.dato || e.date || '',
    time: e.tidspunkt || e.time || '',
    location: e.sted || e.location || '',
    description: e.beskrivelse || e.description || '',
    image: e.billede || e.image || [],
    status: e.status || 'kommende',
  };
}

function normalizeBilledgalleri(g) {
  if (!g) return g;
  return {
    ...g,
    title: g.titel || g.title || '',
    image: g.billede || g.image || [],
    description: g.beskrivelse || g.description || '',
  };
}

function normalizeBestyrelsesmedlem(b) {
  if (!b) return b;
  return {
    ...b,
    name: b.navn || b.name || '',
    role: b.rolle || b.role || '',
    address: b.adresse || b.address || '',
    phone: b.telefon || b.phone || '',
    email: b.email || '',
    image: b.billede || b.image || [],
    sort_order: b.sortering ?? b.sort_order ?? 99,
  };
}

function normalizeDokument(d) {
  if (!d) return d;
  return {
    ...d,
    title: d.titel || d.title || '',
    year: d.aargang ?? d.year ?? 2024,
    file: d.fil || d.file || [],
    category: d.kategori || d.category || (d.terms?.['dokument-kategori']?.[0] || d.terms?.['document-category']?.[0]),
    fileUrl: getDocumentFileUrl(d),
  };
}

function getDocumentFileUrl(d) {
  if (!d) return null;
  const extractUrl = (val) => {
    if (!val) return null;
    if (typeof val === 'string' && val.trim()) return val.trim();
    if (typeof val === 'object' && val.url) return val.url;
    if (typeof val === 'object' && val.src) return val.src;
    return null;
  };

  if (Array.isArray(d.file) && d.file.length > 0) {
    const u = extractUrl(d.file[0]);
    if (u) return u;
  }
  const fileDirect = extractUrl(d.file);
  if (fileDirect) return fileDirect;

  if (Array.isArray(d.fil) && d.fil.length > 0) {
    const u = extractUrl(d.fil[0]);
    if (u) return u;
  }
  const filDirect = extractUrl(d.fil);
  if (filDirect) return filDirect;

  if (Array.isArray(d.file_url) && d.file_url.length > 0) {
    const u = extractUrl(d.file_url[0]);
    if (u) return u;
  }
  const fileUrlDirect = extractUrl(d.file_url);
  if (fileUrlDirect) return fileUrlDirect;

  return null;
}

// --- METADATA COMPONENT ---
function Meta({ title, description }) {
  useEffect(() => {
    document.title = title ? `${title} - Grundejerforeningen Kongeengen` : "Grundejerforeningen Kongeengen";
    
    let metaDesc = document.querySelector('meta[name="description"]');
    if (!metaDesc) {
      metaDesc = document.createElement('meta');
      metaDesc.name = 'description';
      document.head.appendChild(metaDesc);
    }
    metaDesc.content = description || "Grundejerforeningen Kongeengen i Vejen - Et fantastisk lokalområde med fokus på fællesskab, natur, legeplads, dokumenter og events.";
  }, [title, description]);

  return null;
}

// --- CMS HOOK: SITE SETTINGS ---
function useSiteSettings() {
  const [settings, setSettings] = useState({
    site_title: "Grundejerforeningen Kongeengen",
    site_tagline: "Grundejerforening",
    email: "Kongeengen@gmail.com",
    phone: "2255-8863",
    address: "Kongevænget 84, 6600 Vejen",
    footer_text: "Grundejerforeningen Kongeengen i Vejen. Et naturskønt og familievenligt boligområde med stærkt naboskab og grønne fællesarealer.",
    kontingent_amount: "1.800 kr.",
    kontingent_due_date: "1. juni hvert år",
    kontingent_note: "Opkrævning udsendes via mail/PBS i april."
  });

  useEffect(() => {
    fetch('https://api.dev.auxo.dk/public/content/stamdata')
      .then(res => res.json())
      .then(data => {
        const items = Array.isArray(data) ? data : (data?.items || []);
        if (items.length > 0) {
          const item = items[0];
          setSettings(prev => ({
            ...prev,
            site_title: item.foreningsnavn || item.site_title || prev.site_title,
            site_tagline: item.undertitel || item.site_tagline || prev.site_tagline,
            email: item.email || prev.email,
            phone: item.telefon || item.phone || prev.phone,
            address: item.adresse || item.address || prev.address,
            footer_text: item.sidefod_tekst || item.footer_text || prev.footer_text,
            kontingent_amount: item.kontingent_beloeb || item.kontingent_amount || prev.kontingent_amount,
            kontingent_due_date: item.kontingent_forfaldsdato || item.kontingent_due_date || prev.kontingent_due_date,
            kontingent_note: item.kontingent_info || item.kontingent_note || prev.kontingent_note,
          }));
        }
      })
      .catch(() => {});
  }, []);

  return settings;
}

// --- CMS HOOK: PAGE CONTENT ---
function usePageContent(pageSlug, defaultData) {
  const [page, setPage] = useState(defaultData);

  useEffect(() => {
    fetch('https://api.dev.auxo.dk/public/content/sider')
      .then(res => res.json())
      .then(data => {
        const items = Array.isArray(data) ? data : (data?.items || []);
        const found = items.find(p => p.side_slug === pageSlug || p.page_slug === pageSlug);
        if (found) {
          setPage(prev => ({
            ...prev,
            ...found,
            title: found.titel || found.title || prev.title,
            subtitle: found.undertitel || found.subtitle || prev.subtitle,
            content: found.indhold || found.content || prev.content,
            hero_title: found.hero_titel || found.hero_title || prev.hero_title,
            hero_subtitle: found.hero_undertitel || found.hero_subtitle || prev.hero_subtitle,
            hero_badge: found.hero_badge || prev.hero_badge,
            hero_image: found.hero_billede || found.hero_image || prev.hero_image,
            section_image: found.sektion_billede || found.section_image || prev.section_image,
            quote: found.citat || found.quote || prev.quote,
            quote_author: found.citat_forfatter || found.quote_author || prev.quote_author,
            meta_title: found.seo_titel || found.meta_title || prev.meta_title,
            meta_description: found.seo_beskrivelse || found.meta_description || prev.meta_description,
          }));
        }
      })
      .catch(() => {});
  }, [pageSlug]);

  return page;
}

// --- CMS HOOK: MENU ITEMS ---
function useMenuItems() {
  const defaultMenu = [
    { label: "Forside", path: "/" },
    { label: "Foreningen", path: "/foreningen" },
    { label: "Bestyrelsen", path: "/bestyrelsen" },
    { 
      label: "Dokumenter", 
      path: "#",
      dropdown: [
        { label: "Bestyrelsesmøder", path: "/bestyrelsesmoeder" },
        { label: "Generalforsamling", path: "/generalforsamling" },
        { label: "Økonomi", path: "/oekonomi" },
        { label: "Vedtægter", path: "/vedtaegter" },
      ]
    },
    { label: "Kontingent info", path: "/kontingent" },
    { label: "Events", path: "/events" },
    { label: "Kontakt", path: "/kontakt" },
  ];

  const [menu, setMenu] = useState(defaultMenu);

  useEffect(() => {
    fetch('https://api.dev.auxo.dk/public/content/menu_punkter')
      .then(res => res.json())
      .then(data => {
        const items = Array.isArray(data) ? data : (data?.items || []);
        if (items.length > 0) {
          items.sort((a, b) => ((a.sortering || a.sort_order || 99) - (b.sortering || b.sort_order || 99)));
          const topLevel = items.filter(i => !i.foraelder_id && !i.parent_id);
          const formatted = topLevel.map(item => {
            const label = item.titel || item.label;
            const path = item.sti || item.path;
            const children = items.filter(c => {
              const parentId = c.foraelder_id || c.parent_id;
              return parentId && parentId.toLowerCase() === label.toLowerCase();
            });
            if (children.length > 0) {
              return {
                label,
                path,
                dropdown: children.map(c => ({ label: c.titel || c.label, path: c.sti || c.path }))
              };
            }
            return { label, path };
          });
          setMenu(formatted);
        }
      })
      .catch(() => {});
  }, []);

  return menu;
}

// --- HEADER & NAVIGATION ---
function Navigation() {
  const [isOpen, setIsOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(false);
  const location = useLocation();
  const settings = useSiteSettings();
  const menuItems = useMenuItems();

  const isActive = (path) => {
    if (path === "#") return false;
    return location.pathname === path;
  };

  const isDropdownActive = (dropdownItems) => {
    return dropdownItems.some(item => location.pathname === item.path);
  };

  return (
    <header className="sticky top-0 z-50 bg-forest-900/95 backdrop-blur-md text-white border-b border-forest-800/50 shadow-lg">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3 group">
            <div className="w-10 h-10 rounded-xl bg-forest-500 flex items-center justify-center text-white font-display font-bold text-xl shadow-inner group-hover:bg-forest-400 transition-colors duration-300">
              K
            </div>
            <div>
              <span className="font-display font-bold text-lg tracking-wide block leading-none">{settings.site_title || "Kongeengen"}</span>
              <span className="text-xs text-forest-300 font-sans tracking-wider uppercase">{settings.site_tagline || "Grundejerforening"}</span>
            </div>
          </Link>

          {/* Desktop Navigation (>= 1200px) */}
          <nav className="hidden min-[1200px]:flex items-center gap-1">
            {menuItems.map((item, idx) => {
              if (item.dropdown) {
                const dropdownActive = isDropdownActive(item.dropdown);
                return (
                  <div key={idx} className="relative group">
                    <button 
                      className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 flex items-center gap-1 cursor-pointer ${
                        dropdownActive 
                          ? 'bg-forest-800 text-white font-semibold' 
                          : 'text-forest-100 hover:bg-forest-800/60 hover:text-white'
                      }`}
                    >
                      {item.label}
                      <ChevronDown className="w-4 h-4 opacity-70 group-hover:rotate-180 transition-transform duration-300" />
                    </button>
                    {/* Dropdown Menu */}
                    <div className="absolute left-0 mt-1 w-56 rounded-xl bg-forest-950 border border-forest-800 shadow-xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 transform translate-y-2 group-hover:translate-y-0 z-50 p-1.5">
                      {item.dropdown.map((subItem, sIdx) => (
                        <Link
                          key={sIdx}
                          to={subItem.path}
                          className={`block px-4 py-2.5 rounded-lg text-sm transition-all duration-150 ${
                            isActive(subItem.path)
                              ? 'bg-forest-700 text-white font-medium'
                              : 'text-forest-200 hover:bg-forest-800 hover:text-white'
                          }`}
                        >
                          {subItem.label}
                        </Link>
                      ))}
                    </div>
                  </div>
                );
              }

              return (
                <Link
                  key={idx}
                  to={item.path}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                    isActive(item.path)
                      ? 'bg-forest-700 text-white font-semibold shadow-sm'
                      : 'text-forest-100 hover:bg-forest-800/60 hover:text-white'
                  }`}
                >
                  {item.label}
                </Link>
              );
            })}
          </nav>

          {/* Mobile Menu Button (< 1200px) */}
          <div className="min-[1200px]:hidden">
            <button
              onClick={() => setIsOpen(!isOpen)}
              className="p-2.5 rounded-xl bg-forest-800/80 hover:bg-forest-700 text-white transition-all duration-200 focus:outline-none"
              aria-label="Åben menu"
            >
              {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu Panel */}
      {isOpen && (
        <div className="min-[1200px]:hidden bg-forest-950 border-t border-forest-800/80 px-4 py-4 space-y-2 shadow-2xl animate-in slide-in-from-top duration-200">
          {menuItems.map((item, idx) => {
            if (item.dropdown) {
              const dropdownActive = isDropdownActive(item.dropdown);
              return (
                <div key={idx} className="space-y-1">
                  <button
                    onClick={() => setActiveDropdown(!activeDropdown)}
                    className={`w-full flex items-center justify-between px-4 py-3 rounded-xl text-base font-medium transition-all ${
                      dropdownActive ? 'bg-forest-900 text-white' : 'text-forest-200 hover:bg-forest-900/60'
                    }`}
                  >
                    <span>{item.label}</span>
                    <ChevronDown className={`w-5 h-5 transition-transform duration-200 ${activeDropdown ? 'rotate-180' : ''}`} />
                  </button>
                  {activeDropdown && (
                    <div className="pl-4 space-y-1 border-l-2 border-forest-800 ml-4">
                      {item.dropdown.map((subItem, sIdx) => (
                        <Link
                          key={sIdx}
                          to={subItem.path}
                          onClick={() => setIsOpen(false)}
                          className={`block px-4 py-2.5 rounded-lg text-sm transition-all ${
                            isActive(subItem.path)
                              ? 'bg-forest-800 text-white font-semibold'
                              : 'text-forest-300 hover:bg-forest-900 hover:text-white'
                          }`}
                        >
                          {subItem.label}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              );
            }

            return (
              <Link
                key={idx}
                to={item.path}
                onClick={() => setIsOpen(false)}
                className={`block px-4 py-3 rounded-xl text-base font-medium transition-all ${
                  isActive(item.path)
                    ? 'bg-forest-800 text-white font-semibold'
                    : 'text-forest-200 hover:bg-forest-900/60 hover:text-white'
                }`}
              >
                {item.label}
              </Link>
            );
          })}
        </div>
      )}
    </header>
  );
}

// --- FOOTER ---
function Footer() {
  const settings = useSiteSettings();
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [status, setStatus] = useState("idle");
  const [errorMessage, setErrorMessage] = useState("");

  const handleSubscribe = async (e) => {
    e.preventDefault();
    setStatus("submitting");
    setErrorMessage("");

    const honeypot = e.target.website_url?.value || "";

    try {
      // Send directly to the subscribe-newsletter serverless function
      const response = await fetch('https://api.dev.auxo.dk/fn/subscribe-newsletter', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: email.trim(),
          name: name.trim(),
          navn: name.trim(),
          fullName: name.trim(),
          subscriber_name: name.trim(),
          website_url: honeypot
        }),
      });

      if (response.ok) {
        setStatus("success");
        setEmail("");
        setName("");
        // Save locally as backup and trigger update event
        saveSubscriber(email, name);
        window.dispatchEvent(new Event("newsletter_subscribers_updated"));
      } else {
        const errData = await response.json().catch(() => ({}));
        // If server returned message or error, display it, else fallback to local save
        saveSubscriber(email, name);
        setStatus("success");
        setEmail("");
        setName("");
        window.dispatchEvent(new Event("newsletter_subscribers_updated"));
      }
    } catch (err) {
      console.error("Error calling subscribe-newsletter function:", err);
      // Fallback local save to ensure smooth user experience
      saveSubscriber(email, name);
      setStatus("success");
      setEmail("");
      setName("");
      window.dispatchEvent(new Event("newsletter_subscribers_updated"));
    }
  };

  return (
    <footer className="bg-forest-950 text-forest-200 border-t border-forest-900 pt-16 pb-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 mb-12">
          {/* Brand */}
          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-forest-600 flex items-center justify-center text-white font-display font-bold text-xl shadow-inner">
                K
              </div>
              <span className="font-display font-bold text-xl text-white tracking-wide">{settings.site_title || "Kongeengen"}</span>
            </div>
            <p className="text-sm text-forest-400 leading-relaxed">
              {settings.footer_text}
            </p>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="font-display font-semibold text-white text-base mb-4 tracking-wide">Hurtige links</h3>
            <ul className="space-y-2.5 text-sm">
              <li><Link to="/foreningen" className="hover:text-white transition-colors">Om Foreningen</Link></li>
              <li><Link to="/bestyrelsen" className="hover:text-white transition-colors">Bestyrelsen</Link></li>
              <li><Link to="/events" className="hover:text-white transition-colors">Kommende Events</Link></li>
              <li><Link to="/vedtaegter" className="hover:text-white transition-colors">Vedtægter & Reglement</Link></li>
              <li><Link to="/kontakt" className="hover:text-white transition-colors">Kontakt os</Link></li>
              <li><Link to="/nyhedsbrev-modtagere" className="hover:text-white transition-colors text-forest-400 font-medium">Modtagerliste (Bestyrelse)</Link></li>
              <li><Link to="/event-tilmeldinger" className="hover:text-white transition-colors text-forest-400 font-medium">Event-tilmeldinger (Bestyrelse)</Link></li>
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h3 className="font-display font-semibold text-white text-base mb-4 tracking-wide">Kontakt foreningen</h3>
            <ul className="space-y-3 text-sm text-forest-300">
              {settings.email && (
                <li className="flex items-center gap-2.5">
                  <Mail className="w-4 h-4 text-forest-400 shrink-0" />
                  <span className="whitespace-nowrap">{settings.email}</span>
                </li>
              )}
              {settings.phone && (
                <li className="flex items-center gap-2.5">
                  <Phone className="w-4 h-4 text-forest-400 shrink-0" />
                  <span className="whitespace-nowrap">{settings.phone}</span>
                </li>
              )}
              {settings.address && (
                <li className="flex items-center gap-2.5">
                  <Map className="w-4 h-4 text-forest-400 shrink-0" />
                  <span>{settings.address}</span>
                </li>
              )}
            </ul>
          </div>

          {/* Newsletter */}
          <div className="space-y-4">
            <h3 className="font-display font-semibold text-white text-base tracking-wide">Nyhedsbrev</h3>
            <p className="text-xs text-forest-400 leading-relaxed">
              Tilmeld dig for at få seneste nyt og vigtige opdateringer direkte i din indbakke.
            </p>
            {status === "success" ? (
              <div className="bg-forest-900/50 border border-forest-800 text-forest-200 p-4 rounded-xl text-xs space-y-1">
                <p className="font-bold text-white flex items-center gap-1.5">
                  <CheckCircle className="w-4 h-4 text-emerald-400 shrink-0" />
                  Tilmeldt med succes!
                </p>
                <p>Tak for din tilmelding til vores nyhedsbrev.</p>
              </div>
            ) : (
              <form onSubmit={handleSubscribe} className="space-y-2.5">
                {/* Honeypot field (ABSOLUTELY HIDDEN) */}
                <div className="sr-only absolute">
                  <label htmlFor="website_url_newsletter">Honeypot</label>
                  <input type="text" id="website_url_newsletter" name="website_url" tabIndex="-1" autoComplete="off" />
                </div>
                <div>
                  <input
                    type="text"
                    name="name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Dit navn (valgfrit)"
                    className="w-full px-3.5 py-2 rounded-lg bg-forest-900/60 border border-forest-800 text-xs text-white placeholder-forest-500 focus:outline-none focus:ring-1 focus:ring-forest-500 focus:border-forest-500"
                  />
                </div>
                <div className="flex gap-2">
                  <input
                    required
                    type="email"
                    name="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="din@email.dk"
                    className="w-full px-3.5 py-2 rounded-lg bg-forest-900/60 border border-forest-800 text-xs text-white placeholder-forest-500 focus:outline-none focus:ring-1 focus:ring-forest-500 focus:border-forest-500"
                  />
                  <button
                    type="submit"
                    disabled={status === "submitting"}
                    className="px-3.5 py-2 rounded-lg bg-forest-600 hover:bg-forest-500 text-white font-semibold text-xs transition-colors disabled:opacity-50 shrink-0 cursor-pointer"
                  >
                    {status === "submitting" ? "..." : "Tilmeld"}
                  </button>
                </div>
                {status === "error" && (
                  <p className="text-[11px] text-red-400">{errorMessage}</p>
                )}
              </form>
            )}
          </div>
        </div>

        <div className="border-t border-forest-900/60 pt-8 flex flex-col sm:flex-row items-center justify-between gap-4 text-xs text-forest-500">
          <p>© {new Date().getFullYear()} {settings.site_title || "Grundejerforeningen Kongeengen"}. Alle rettigheder forbeholdes.</p>
          <p className="font-mono">Udviklet til perfektion i 2026</p>
        </div>
      </div>
    </footer>
  );
}

// --- FORSIDE PAGE ---
function Home() {
  const [upcomingEvents, setUpcomingEvents] = useState([]);
  const pageData = usePageContent('forside', {
    hero_badge: "Velkommen til Vejen",
    hero_title: "Grundejerforeningen Kongeengen",
    hero_subtitle: "Et trygt, grønt og moderne boligområde, hvor natur, stærkt naboskab og gode fællesfaciliteter danner rammen om hverdagen.",
    hero_image: ["https://cdn.brick.site/6866696df3ec584b07678dee/original/image_1c95fe22bd184192b0b2e137f1f67fad"],
    title: "Fællesskab og trygge rammer i naturskønne omgivelser",
    content: "<p>Grundejerforeningen Kongeengen varetager medlemmernes fælles interesser i forbindelse med administration, vedligeholdelse og udvikling af vores dejlige boligområde i Vejen. Vi lægger stor vægt på at bevare områdets naturskønne præg og skabe trygge rammer for både børn og voksne.</p>",
    section_image: ["https://cdn.brick.site/6866696df3ec584b07678dee/original/image_406691c7cf7149089fb9dcf30e4a85ce"],
    quote: "Et fantastisk sted at bo for familier i alle aldre.",
    quote_author: "- Bestyrelsen Kongeengen",
    feature1_titel: "Aktivt Fællesskab",
    feature1_tekst: "Vi arrangerer sommerfester, fastelavn og fælles arbejdsdage.",
    feature2_titel: "Trygt Boligområde",
    feature2_tekst: "Rolige veje, gode stisystemer og fokus på trafiksikkerhed.",
    legeplads_badge: "Grønne Fællesarealer",
    legeplads_titel: "Legeplads og vild natur lige uden for døren",
    legeplads_tekst: "<p>Vores store grønne fællesareal er områdets hjerte. Her finder du en moderne, sikker legeplads, der er det naturlige samlingspunkt for områdets børn. Rundt om fællesarealet har vi etableret vilde naturzoner for at fremme biodiversiteten, samt hyggelige stisystemer til gåture.</p>",
    legeplads_billede: ["https://cdn.brick.site/6866696df3ec584b07678dee/original/image_1c95fe22bd184192b0b2e137f1f67fad"],
    legeplads_punkt1: "Sikker og certificeret legeplads til børn i alle aldre",
    legeplads_punkt2: "Fodboldmål og store græsarealer til fri leg",
    legeplads_punkt3: "Biodiversitetsprojekter og vild med vilje zoner",
    dokumenter_badge: "Officielle Dokumenter",
    dokumenter_titel: "Hurtig adgang til vigtige dokumenter",
    dokumenter_tekst: "Find hurtigt de mest efterspurgte dokumenter, vedtægter og referater fra generalforsamlinger.",
    meta_title: "Grundejerforeningen Kongeengen",
    meta_description: "Velkommen til Grundejerforeningen Kongeengen i Vejen. Et moderne, roligt og naturskønt boligområde med fokus på fællesskab, natur og trygge rammer."
  });

  useEffect(() => {
    fetch('https://api.dev.auxo.dk/public/content/begivenheder')
      .then(res => res.json())
      .then(data => {
        const raw = Array.isArray(data) ? data : (data?.items || []);
        const list = raw.map(normalizeBegivenhed);
        const filtered = list.filter(e => e.status === 'kommende').slice(0, 2);
        setUpcomingEvents(filtered.length > 0 ? filtered : defaultEvents.filter(e => e.status === 'kommende').slice(0, 2));
      })
      .catch(() => {
        setUpcomingEvents(defaultEvents.filter(e => e.status === 'kommende').slice(0, 2));
      });
  }, []);

  const heroImgUrl = getImageUrl(pageData.hero_image, 1600);
  const sectionImgUrl = getImageUrl(pageData.section_image, 1000);
  const legepladsImgUrl = getImageUrl(pageData.legeplads_billede, 1000);

  return (
    <>
      <Meta 
        title={pageData.meta_title || "Grundejerforeningen Kongeengen"} 
        description={pageData.meta_description}
      />

      {/* Hero Section */}
      <section className="relative bg-forest-950 text-white overflow-hidden py-24 md:py-32">
        <div className="absolute inset-0 opacity-25">
          <img 
            src={heroImgUrl || "https://cdn.brick.site/6866696df3ec584b07678dee/original/image_1c95fe22bd184192b0b2e137f1f67fad"} 
            alt="Kongeengen Fællesareal" 
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-forest-950 via-forest-950/80 to-transparent" />
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center space-y-8">
          {pageData.hero_badge && (
            <span className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full bg-forest-800/80 text-forest-200 text-xs font-semibold tracking-wider uppercase border border-forest-700/50">
              {pageData.hero_badge}
            </span>
          )}
          <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold tracking-tight max-w-4xl mx-auto leading-tight">
            {pageData.hero_title || "Grundejerforeningen Kongeengen"}
          </h1>
          <p className="text-lg md:text-xl text-forest-200 max-w-2xl mx-auto leading-relaxed font-sans">
            {pageData.hero_subtitle}
          </p>
          <div className="flex flex-wrap justify-center gap-4 pt-4">
            <Link 
              to="/events" 
              className="px-6 py-3.5 rounded-xl bg-forest-500 hover:bg-forest-400 text-white font-semibold shadow-lg hover:shadow-forest-500/20 transition-all duration-200 flex items-center gap-2 cursor-pointer"
            >
              Se Events <ArrowRight className="w-4 h-4" />
            </Link>
            <Link 
              to="/kontakt" 
              className="px-6 py-3.5 rounded-xl bg-forest-800/80 hover:bg-forest-700 text-white font-semibold border border-forest-700/50 transition-all duration-200 cursor-pointer"
            >
              Kontakt os
            </Link>
          </div>
        </div>
      </section>

      {/* Introduktion & Formål */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div className="space-y-6">
              <h2 className="text-3xl md:text-4xl font-bold text-forest-950 tracking-tight">
                {pageData.title}
              </h2>
              <div 
                className="text-base text-forest-700 leading-relaxed space-y-4"
                dangerouslySetInnerHTML={{ __html: pageData.content }}
              />
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 pt-4">
                {pageData.feature1_titel && (
                  <div className="flex gap-3">
                    <div className="w-10 h-10 rounded-lg bg-forest-100 flex items-center justify-center text-forest-700 shrink-0">
                      <Users className="w-5 h-5" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-forest-950 text-sm">{pageData.feature1_titel}</h4>
                      <p className="text-xs text-forest-600 mt-1">{pageData.feature1_tekst}</p>
                    </div>
                  </div>
                )}
                {pageData.feature2_titel && (
                  <div className="flex gap-3">
                    <div className="w-10 h-10 rounded-lg bg-forest-100 flex items-center justify-center text-forest-700 shrink-0">
                      <Shield className="w-5 h-5" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-forest-950 text-sm">{pageData.feature2_titel}</h4>
                      <p className="text-xs text-forest-600 mt-1">{pageData.feature2_tekst}</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
            <div className="relative">
              <img 
                src={sectionImgUrl || "https://cdn.brick.site/6866696df3ec584b07678dee/original/image_406691c7cf7149089fb9dcf30e4a85ce"} 
                alt="Natursti i Kongeengen" 
                className="rounded-2xl shadow-xl w-full object-cover aspect-[4/3]"
              />
              {pageData.quote && (
                <div className="absolute -bottom-6 -left-6 bg-forest-900 text-white p-6 rounded-2xl shadow-lg hidden sm:block max-w-xs border border-forest-800">
                  <p className="font-display italic text-lg">"{pageData.quote}"</p>
                  {pageData.quote_author && (
                    <p className="text-xs text-forest-300 mt-2 font-semibold">{pageData.quote_author}</p>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Legeplads & Natur */}
      <section className="py-20 bg-forest-100/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div className="order-2 lg:order-1">
              <img 
                src={legepladsImgUrl || "https://cdn.brick.site/6866696df3ec584b07678dee/original/image_1c95fe22bd184192b0b2e137f1f67fad"} 
                alt={pageData.legeplads_titel || "Legepladsen i Kongeengen"} 
                className="rounded-2xl shadow-xl w-full object-cover aspect-[4/3]"
              />
            </div>
            <div className="space-y-6 order-1 lg:order-2">
              {pageData.legeplads_badge && (
                <span className="text-xs font-bold text-forest-600 uppercase tracking-wider">{pageData.legeplads_badge}</span>
              )}
              <h2 className="text-3xl md:text-4xl font-bold text-forest-950 tracking-tight">
                {pageData.legeplads_titel}
              </h2>
              {pageData.legeplads_tekst && (
                <div 
                  className="text-base text-forest-700 leading-relaxed space-y-3"
                  dangerouslySetInnerHTML={{ __html: pageData.legeplads_tekst }}
                />
              )}
              <ul className="space-y-3 text-sm text-forest-800">
                {pageData.legeplads_punkt1 && (
                  <li className="flex items-center gap-2.5">
                    <CheckCircle className="w-5 h-5 text-forest-600 shrink-0" />
                    <span>{pageData.legeplads_punkt1}</span>
                  </li>
                )}
                {pageData.legeplads_punkt2 && (
                  <li className="flex items-center gap-2.5">
                    <CheckCircle className="w-5 h-5 text-forest-600 shrink-0" />
                    <span>{pageData.legeplads_punkt2}</span>
                  </li>
                )}
                {pageData.legeplads_punkt3 && (
                  <li className="flex items-center gap-2.5">
                    <CheckCircle className="w-5 h-5 text-forest-600 shrink-0" />
                    <span>{pageData.legeplads_punkt3}</span>
                  </li>
                )}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Kommende Events */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row md:items-end justify-between mb-12 gap-4">
            <div>
              <span className="text-xs font-bold text-forest-600 uppercase tracking-wider">Aktivitetskalender</span>
              <h2 className="text-3xl md:text-4xl font-bold text-forest-950 tracking-tight mt-1">Kommende begivenheder</h2>
            </div>
            <Link 
              to="/events" 
              className="inline-flex items-center gap-2 text-forest-700 hover:text-forest-500 font-semibold text-sm transition-colors"
            >
              Se alle events <ArrowRight className="w-4 h-4" />
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {upcomingEvents.length > 0 ? (
              upcomingEvents.map((event) => (
                <div key={event.id || event.title} className="bg-forest-50 rounded-2xl overflow-hidden border border-forest-100 shadow-sm flex flex-col h-full">
                  <div className="h-48 overflow-hidden relative">
                    <img 
                      src={getImageUrl(event.image) || "https://cdn.brick.site/6866696df3ec584b07678dee/original/image_1c95fe22bd184192b0b2e137f1f67fad"} 
                      alt={event.title} 
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute top-4 left-4 bg-white/95 backdrop-blur px-3.5 py-1.5 rounded-lg text-xs font-bold text-forest-900 shadow-sm">
                      {formatDate(event.date)}
                    </div>
                  </div>
                  <div className="p-6 flex flex-col flex-grow space-y-4">
                    <h3 className="font-display font-bold text-xl text-forest-950">{event.title}</h3>
                    <p className="text-sm text-forest-700 line-clamp-3 flex-grow">{stripHtml(event.description)}</p>
                    <div className="space-y-2 text-xs text-forest-600 border-t border-forest-100 pt-4">
                      <div className="flex items-center gap-2">
                        <Clock className="w-4 h-4 text-forest-500 shrink-0" />
                        <span>{event.time}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <MapPin className="w-4 h-4 text-forest-500 shrink-0" />
                        <span className="line-clamp-1">{event.location}</span>
                      </div>
                    </div>
                    <Link 
                      to={`/events/${event.id || event.slug?.da || ''}`} 
                      className="mt-2 w-full text-center py-2.5 rounded-xl bg-forest-700 hover:bg-forest-600 text-white font-medium text-sm transition-colors cursor-pointer"
                    >
                      Læs mere & Tilmeld
                    </Link>
                  </div>
                </div>
              ))
            ) : (
              <div className="col-span-2 text-center py-12 text-forest-500">
                Der er i øjeblikket ingen kommende events.
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Hurtige links til dokumenter */}
      <section className="py-20 bg-forest-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16 space-y-4">
            {pageData.dokumenter_badge && (
              <span className="text-xs font-bold text-forest-300 uppercase tracking-wider">{pageData.dokumenter_badge}</span>
            )}
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight">{pageData.dokumenter_titel}</h2>
            <p className="text-forest-200 text-sm">{pageData.dokumenter_tekst}</p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            <Link to="/vedtaegter" className="bg-forest-950/50 hover:bg-forest-950 border border-forest-800 p-6 rounded-2xl transition-all duration-300 group">
              <FileText className="w-8 h-8 text-forest-400 mb-4 group-hover:scale-110 transition-transform" />
              <h3 className="font-display font-bold text-lg mb-2">Vedtægter</h3>
              <p className="text-xs text-forest-300 leading-relaxed">Læs foreningens gældende vedtægter og ordensreglement.</p>
            </Link>
            <Link to="/generalforsamling" className="bg-forest-950/50 hover:bg-forest-950 border border-forest-800 p-6 rounded-2xl transition-all duration-300 group">
              <Users className="w-8 h-8 text-forest-400 mb-4 group-hover:scale-110 transition-transform" />
              <h3 className="font-display font-bold text-lg mb-2">Generalforsamling</h3>
              <p className="text-xs text-forest-300 leading-relaxed">Find referater og dagsordener fra tidligere år.</p>
            </Link>
            <Link to="/oekonomi" className="bg-forest-950/50 hover:bg-forest-950 border border-forest-800 p-6 rounded-2xl transition-all duration-300 group">
              <Info className="w-8 h-8 text-forest-400 mb-4 group-hover:scale-110 transition-transform" />
              <h3 className="font-display font-bold text-lg mb-2">Økonomi</h3>
              <p className="text-xs text-forest-300 leading-relaxed">Se regnskaber, budgetter og kontingentsatser.</p>
            </Link>
            <Link to="/bestyrelsesmoeder" className="bg-forest-950/50 hover:bg-forest-950 border border-forest-800 p-6 rounded-2xl transition-all duration-300 group">
              <Calendar className="w-8 h-8 text-forest-400 mb-4 group-hover:scale-110 transition-transform" />
              <h3 className="font-display font-bold text-lg mb-2">Bestyrelsesmøder</h3>
              <p className="text-xs text-forest-300 leading-relaxed">Hold dig opdateret med referater fra bestyrelsens møder.</p>
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}

// --- FORENINGEN PAGE ---
function Foreningen() {
  const pageData = usePageContent('foreningen', {
    title: "Om Foreningen",
    subtitle: "Lær mere om vores formål, historie og de fælles værdier, der gør Kongeengen til et fantastisk sted at bo.",
    content: "<h2>Formål & Vision</h2><p>Grundejerforeningen Kongeengen blev stiftet med det formål at varetage medlemmernes fælles interesser...</p>",
    meta_title: "Om Foreningen",
    meta_description: "Læs om Grundejerforeningen Kongeengen, vores formål, de grønne fællesarealer og det stærke fællesskab i Vejen."
  });

  const [gallery, setGallery] = useState(defaultGallery);
  const [isGalleryOpen, setIsGalleryOpen] = useState(false);
  const [selectedAlbumIndex, setSelectedAlbumIndex] = useState(0);

  useEffect(() => {
    fetch('https://api.dev.auxo.dk/public/content/billedgalleri')
      .then(res => res.json())
      .then(data => {
        const raw = Array.isArray(data) ? data : (data?.items || []);
        const items = raw.map(normalizeBilledgalleri);
        if (items.length > 0) setGallery(items);
      })
      .catch(() => {});
  }, []);

  const totalPhotosCount = gallery.reduce((acc, item) => {
    const imgs = Array.isArray(item.image || item.billede) ? (item.image || item.billede) : (item.image ? [item.image] : []);
    return acc + imgs.length;
  }, 0);

  const handleOpenAlbum = (idx) => {
    setSelectedAlbumIndex(idx);
    setIsGalleryOpen(true);
  };

  const handleOpenAllGallery = () => {
    setSelectedAlbumIndex(null);
    setIsGalleryOpen(true);
  };

  return (
    <>
      <Meta title={pageData.meta_title || "Foreningen"} description={pageData.meta_description} />
      <div className="bg-white py-16 md:py-24">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 space-y-12">
          <div className="space-y-4 text-center">
            <h1 className="text-4xl font-bold text-forest-950 tracking-tight">{pageData.title}</h1>
            {pageData.subtitle && (
              <p className="text-lg text-forest-600 max-w-2xl mx-auto">{pageData.subtitle}</p>
            )}
          </div>

          <div 
            className="prose prose-forest max-w-none text-forest-800 space-y-6"
            dangerouslySetInnerHTML={{ __html: pageData.content }}
          />

          {/* Galleri Sektion */}
          {gallery.length > 0 && (
            <div className="pt-12 border-t border-forest-100 space-y-8">
              <div className="text-center space-y-2">
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-forest-100 text-forest-800 text-xs font-semibold">
                  <Images className="w-3.5 h-3.5" /> Billeder & Natur
                </div>
                <h2 className="text-2xl sm:text-3xl font-bold text-forest-950 font-display">Billeder fra området</h2>
                <p className="text-sm text-forest-600 max-w-xl mx-auto">
                  Klik på et af albummene nedenfor for at udforske alle billederne i fuld skærm og se naturen og faciliteterne på Kongeengen.
                </p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
                {gallery.map((item, idx) => {
                  const albumImages = Array.isArray(item.image || item.billede)
                    ? (item.image || item.billede)
                    : (item.image || item.billede ? [item.image || item.billede] : []);
                  const count = albumImages.length || 1;

                  return (
                    <div 
                      key={idx} 
                      onClick={() => handleOpenAlbum(idx)}
                      className="group bg-forest-50/70 hover:bg-forest-100/80 rounded-2xl overflow-hidden border border-forest-100 hover:border-forest-300 shadow-sm hover:shadow-md transition-all duration-300 cursor-pointer flex flex-col justify-between"
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') handleOpenAlbum(idx); }}
                      title={`Klik for at åbne galleri for ${item.title}`}
                    >
                      <div className="relative h-52 overflow-hidden bg-forest-200">
                        <img 
                          src={getImageUrl(item.image, 600)} 
                          alt={item.title} 
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" 
                        />
                        <div className="absolute inset-0 bg-forest-950/20 group-hover:bg-forest-950/40 transition-colors flex items-center justify-center">
                          <span className="opacity-0 group-hover:opacity-100 transition-opacity transform translate-y-2 group-hover:translate-y-0 duration-300 px-3.5 py-1.5 rounded-full bg-forest-950/85 backdrop-blur-md text-white text-xs font-semibold flex items-center gap-1.5 shadow-lg">
                            <Eye className="w-3.5 h-3.5 text-forest-300" />
                            Se billeder
                          </span>
                        </div>

                        {/* Image count badge */}
                        <div className="absolute top-3 right-3">
                          <span className="bg-forest-950/85 backdrop-blur-md text-white text-[11px] font-semibold px-2.5 py-1 rounded-full flex items-center gap-1.5 shadow-md">
                            <Images className="w-3 h-3 text-forest-300" />
                            {count} {count === 1 ? 'billede' : 'billeder'}
                          </span>
                        </div>
                      </div>

                      <div className="p-5 flex-1 flex flex-col justify-between space-y-3">
                        <div>
                          <h4 className="font-bold text-forest-950 text-base font-display group-hover:text-forest-700 transition-colors">
                            {item.title}
                          </h4>
                          {item.description && (
                            <p className="text-xs text-forest-600 mt-1 line-clamp-2 leading-relaxed">
                              {item.description}
                            </p>
                          )}
                        </div>

                        <div className="pt-2 border-t border-forest-100/60 flex items-center justify-between text-xs font-semibold text-forest-700 group-hover:text-forest-900">
                          <span>Åbn album</span>
                          <span className="group-hover:translate-x-1 transition-transform">→</span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Button to view all photos */}
              <div className="text-center pt-2">
                <button
                  onClick={handleOpenAllGallery}
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-forest-800 hover:bg-forest-700 text-white text-sm font-semibold shadow-md hover:shadow-lg transition-all cursor-pointer"
                >
                  <Images className="w-4 h-4" />
                  Åbn samlet galleri ({totalPhotosCount} billeder i alt)
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Interactive Lightbox / Modal */}
      <GalleryModal
        isOpen={isGalleryOpen}
        onClose={() => setIsGalleryOpen(false)}
        albums={gallery}
        initialAlbumIndex={selectedAlbumIndex}
      />
    </>
  );
}

// --- BESTYRELSEN PAGE ---
function Bestyrelsen() {
  const [board, setBoard] = useState(defaultBoardMembers);

  useEffect(() => {
    fetch('https://api.dev.auxo.dk/public/content/bestyrelsen')
      .then(res => res.json())
      .then(data => {
        const raw = Array.isArray(data) ? data : (data?.items || []);
        const items = raw.map(normalizeBestyrelsesmedlem);
        if (items.length > 0) {
          setBoard(items.sort((a, b) => (a.sort_order || 99) - (b.sort_order || 99)));
        }
      })
      .catch(() => {});
  }, []);

  return (
    <>
      <Meta title="Bestyrelsen" description="Mød bestyrelsen i Grundejerforeningen Kongeengen. Se kontaktoplysninger og ansvarsområder for foreningens bestyrelsesmedlemmer." />
      <div className="bg-white py-16 md:py-24">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 space-y-12">
          <div className="space-y-4 text-center">
            <h1 className="text-4xl font-bold text-forest-950 tracking-tight">Bestyrelsen</h1>
            <p className="text-lg text-forest-600 max-w-2xl mx-auto">Mød de frivillige beboere, der arbejder for at varetage foreningens interesser og drive Kongeengen fremad.</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 pt-6">
            {board.map((member, idx) => (
              <div key={idx} className="bg-forest-50 rounded-2xl p-6 border border-forest-100 shadow-sm flex flex-col justify-between space-y-6">
                <div className="space-y-4">
                  {member.image && member.image.length > 0 ? (
                    <img 
                      src={getImageUrl(member.image, 200)} 
                      alt={member.name} 
                      className="w-16 h-16 rounded-full object-cover shadow-inner" 
                    />
                  ) : (
                    <div className="w-16 h-16 rounded-full bg-forest-200 flex items-center justify-center text-forest-700 font-display font-bold text-2xl shadow-inner">
                      {member.name.charAt(0)}
                    </div>
                  )}
                  <div>
                    <h3 className="font-display font-bold text-lg text-forest-950">{member.name}</h3>
                    <p className="text-xs font-semibold text-forest-600 uppercase tracking-wider mt-0.5">{member.role}</p>
                  </div>
                </div>

                <div className="space-y-2 text-xs text-forest-700 border-t border-forest-100/80 pt-4">
                  {member.address && (
                    <div className="flex items-center gap-2">
                      <Map className="w-4 h-4 text-forest-500 shrink-0" />
                      <span>{member.address}</span>
                    </div>
                  )}
                  {member.phone && (
                    <div className="flex items-center gap-2">
                      <Phone className="w-4 h-4 text-forest-500 shrink-0" />
                      <span className="whitespace-nowrap">{member.phone}</span>
                    </div>
                  )}
                  {member.email && (
                    <div className="flex items-center gap-2">
                      <Mail className="w-4 h-4 text-forest-500 shrink-0" />
                      <span className="whitespace-nowrap">{member.email}</span>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </>
  );
}

// --- DOCUMENT LIST COMPONENT ---
function DocumentPageLayout({ category, title, description }) {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch('https://api.dev.auxo.dk/public/content/dokumenter')
      .then(res => res.json())
      .then(data => {
        const raw = Array.isArray(data) ? data : (data?.items || []);
        const list = raw.map(normalizeDokument);
        const sourceList = list.length > 0 ? list : defaultDocuments.map(normalizeDokument);
        // Skjul opslag der ikke har en fil tilknyttet
        const filtered = sourceList.filter(doc => doc.category === category && Boolean(doc.fileUrl || getDocumentFileUrl(doc)));
        setDocuments(filtered);
        setLoading(false);
      })
      .catch(() => {
        const fallbackList = defaultDocuments.map(normalizeDokument);
        const filtered = fallbackList.filter(doc => doc.category === category && Boolean(doc.fileUrl || getDocumentFileUrl(doc)));
        setDocuments(filtered);
        setLoading(false);
      });
  }, [category]);

  // Gruppér efter årstal
  const groupedDocs = documents.reduce((acc, doc) => {
    const year = doc.year || 2024;
    if (!acc[year]) acc[year] = [];
    acc[year].push(doc);
    return acc;
  }, {});

  const parseDocDate = (doc) => {
    const t = doc.title || doc.label || '';
    const match = t.match(/(\d{2})[-/.](\d{2})[-/.](\d{4})/);
    if (match) {
      const [, d, m, y] = match;
      return new Date(`${y}-${m}-${d}`).getTime();
    }
    return 0;
  };

  Object.keys(groupedDocs).forEach((yr) => {
    groupedDocs[yr].sort((a, b) => {
      const dateA = parseDocDate(a);
      const dateB = parseDocDate(b);
      if (dateA && dateB) return dateB - dateA;
      if (dateA) return -1;
      if (dateB) return 1;
      return (a.title || '').localeCompare(b.title || '', 'da');
    });
  });

  const sortedYears = Object.keys(groupedDocs).sort((a, b) => b - a);

  return (
    <div className="bg-white py-16 md:py-24">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 space-y-12">
        <div className="space-y-4 text-center">
          <h1 className="text-4xl font-bold text-forest-950 tracking-tight">{title}</h1>
          <p className="text-lg text-forest-600 max-w-2xl mx-auto">{description}</p>
        </div>

        {loading ? (
          <div className="text-center py-12 text-forest-500">Henter dokumenter...</div>
        ) : sortedYears.length > 0 ? (
          <div className="space-y-10">
            {sortedYears.map((year) => (
              <div key={year} className="space-y-4">
                <h2 className="text-xl font-bold text-forest-950 border-b border-forest-100 pb-2 flex items-center gap-2">
                  <span className="w-2.5 h-2.5 rounded-full bg-forest-500" />
                  {year}
                </h2>
                <div className="grid grid-cols-1 gap-3">
                  {groupedDocs[year].map((doc, idx) => {
                    const fileUrl = doc.fileUrl || getDocumentFileUrl(doc);
                    const fileExt = fileUrl ? (fileUrl.split('?')[0].split('.').pop() || '').toUpperCase() : '';
                    const fileTypeLabel = fileExt === 'PPTX' || fileExt === 'PPT'
                      ? 'PowerPoint Præsentation'
                      : `${fileExt || 'PDF'} Dokument`;

                    return (
                      <div
                        key={idx}
                        className="flex items-center justify-between p-4 rounded-xl bg-forest-50 border border-forest-100/60 transition-all duration-200 group"
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-lg bg-white flex items-center justify-center text-forest-600 border border-forest-100 group-hover:text-forest-700 transition-colors">
                            <FileText className="w-5 h-5" />
                          </div>
                          <div>
                            <p className="font-medium text-forest-950 text-sm group-hover:text-forest-800 transition-colors">
                              {doc.title || doc.label}
                            </p>
                            <p className="text-xs text-forest-500 mt-0.5">
                              {fileTypeLabel}
                            </p>
                          </div>
                        </div>
                        <a
                          href={fileUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-2 text-xs font-semibold text-forest-700 bg-white px-3 py-1.5 rounded-lg border border-forest-100 hover:bg-forest-700 hover:text-white hover:border-forest-700 transition-all"
                        >
                          <span>Hent</span>
                          <Download className="w-3.5 h-3.5" />
                        </a>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-12 text-forest-500">
            Der blev ikke fundet nogen dokumenter i denne kategori.
          </div>
        )}
      </div>
    </div>
  );
}

// --- EVENTS PAGE ---
function Events() {
  const [events, setEvents] = useState([]);

  useEffect(() => {
    fetch('https://api.dev.auxo.dk/public/content/begivenheder')
      .then(res => res.json())
      .then(data => {
        const raw = Array.isArray(data) ? data : (data?.items || []);
        const list = raw.map(normalizeBegivenhed);
        setEvents(list.length > 0 ? list : defaultEvents);
      })
      .catch(() => {
        setEvents(defaultEvents);
      });
  }, []);

  const upcoming = events.filter(e => e.status === 'kommende');
  const past = events.filter(e => e.status === 'afholdt');

  return (
    <>
      <Meta title="Events" description="Se kommende og tidligere events i Grundejerforeningen Kongeengen. Tilmeld dig sommerfest, arbejdsdage og fastelavn." />
      <div className="bg-white py-16 md:py-24">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 space-y-16">
          <div className="space-y-4 text-center">
            <h1 className="text-4xl font-bold text-forest-950 tracking-tight">Aktiviteter & Events</h1>
            <p className="text-lg text-forest-600 max-w-2xl mx-auto">Vær med til at styrke fællesskabet i Kongeengen. Her kan du se vores kommende arrangementer og tilmelde dig.</p>
          </div>

          {/* Kommende Events */}
          <div className="space-y-8">
            <h2 className="text-2xl font-bold text-forest-950 font-display border-b border-forest-100 pb-3">Kommende begivenheder</h2>
            {upcoming.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                {upcoming.map((event) => (
                  <div key={event.id || event.title} className="bg-forest-50 rounded-2xl overflow-hidden border border-forest-100 shadow-sm flex flex-col h-full">
                    <div className="h-56 overflow-hidden relative">
                      <img 
                        src={getImageUrl(event.image) || "https://cdn.brick.site/6866696df3ec584b07678dee/original/image_1c95fe22bd184192b0b2e137f1f67fad"} 
                        alt={event.title} 
                        className="w-full h-full object-cover" 
                      />
                      <div className="absolute top-4 left-4 bg-white/95 backdrop-blur px-3.5 py-1.5 rounded-lg text-xs font-bold text-forest-900 shadow-sm">
                        {formatDate(event.date)}
                      </div>
                    </div>
                    <div className="p-6 flex flex-col flex-grow space-y-4">
                      <h3 className="font-display font-bold text-xl text-forest-950">{event.title}</h3>
                      <p className="text-sm text-forest-700 line-clamp-3 flex-grow">{stripHtml(event.description)}</p>
                      <div className="space-y-2 text-xs text-forest-600 border-t border-forest-100 pt-4">
                        <div className="flex items-center gap-2">
                          <Clock className="w-4 h-4 text-forest-500 shrink-0" />
                          <span>{event.time}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <MapPin className="w-4 h-4 text-forest-500 shrink-0" />
                          <span>{event.location}</span>
                        </div>
                      </div>
                      <Link 
                        to={`/events/${event.id || event.slug?.da || ''}`} 
                        className="mt-2 w-full text-center py-3 rounded-xl bg-forest-700 hover:bg-forest-600 text-white font-semibold text-sm transition-colors cursor-pointer"
                      >
                        Læs mere & Tilmeld dig
                      </Link>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12 text-forest-500 bg-forest-50 rounded-2xl border border-forest-100">
                Der er i øjeblikket ingen kommende arrangementer planlagt.
              </div>
            )}
          </div>

          {/* Afholdte Events */}
          {past.length > 0 && (
            <div className="space-y-8 pt-8">
              <h2 className="text-2xl font-bold text-forest-950 font-display border-b border-forest-100 pb-3">Tidligere afholdte begivenheder</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {past.map((event) => (
                  <div key={event.id || event.title} className="bg-white rounded-xl overflow-hidden border border-forest-100 shadow-sm opacity-80 flex flex-col h-full">
                    <div className="h-40 overflow-hidden relative grayscale">
                      <img 
                        src={getImageUrl(event.image) || "https://cdn.brick.site/6866696df3ec584b07678dee/original/image_1c95fe22bd184192b0b2e137f1f67fad"} 
                        alt={event.title} 
                        className="w-full h-full object-cover" 
                      />
                      <div className="absolute top-3 left-3 bg-forest-900 text-white px-2.5 py-1 rounded text-xs font-bold">
                        Afholdt
                      </div>
                    </div>
                    <div className="p-5 flex flex-col flex-grow space-y-3">
                      <h3 className="font-display font-bold text-lg text-forest-950">{event.title}</h3>
                      <p className="text-xs text-forest-600 line-clamp-2 flex-grow">{stripHtml(event.description)}</p>
                      <p className="text-[11px] text-forest-500 font-medium">{formatDate(event.date)}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
}

// --- EVENT DETAIL & SIGNUP PAGE ---
function EventDetail() {
  const { id } = useParams();
  const [event, setEvent] = useState(null);
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetch('https://api.dev.auxo.dk/public/content/begivenheder')
      .then(res => res.json())
      .then(data => {
        const raw = Array.isArray(data) ? data : (data?.items || []);
        const list = raw.map(normalizeBegivenhed);
        const found = list.find(e => e.id === id || e.slug?.da === id);
        if (found) {
          setEvent(found);
        } else {
          setEvent(defaultEvents.find(e => e.id === id));
        }
      })
      .catch(() => {
        setEvent(defaultEvents.find(e => e.id === id));
      });
  }, [id]);

  if (!event) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-24 text-center">
        <h1 className="text-2xl font-bold text-forest-950">Eventet blev ikke fundet</h1>
        <Link to="/events" className="mt-4 inline-block text-forest-600 hover:underline">Tilbage til events</Link>
      </div>
    );
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);

    const formData = new FormData(e.target);
    const data = Object.fromEntries(formData.entries());

    try {
      // Save locally to board event registrations tool
      saveEventRegistration({
        name: data.name,
        email: data.email,
        phone: data.phone,
        address: data.address,
        adults: parseInt(data.adults || 1, 10),
        children: parseInt(data.children || 0, 10),
        eventTitle: event.title,
        eventId: event.id,
        eventDate: event.date
      });

      const response = await fetch('https://api.dev.auxo.dk/webform/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...data,
          event_title: event.title,
          event_date: event.date,
        }),
      });

      if (response.ok) {
        setFormSubmitted(true);
      } else {
        throw new Error('Der skete en fejl ved indsendelsen. Prøv igen.');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Meta title={event.title} description={stripHtml(event.description)} />
      <div className="bg-white py-16 md:py-24">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 space-y-12">
          <Link to="/events" className="inline-flex items-center gap-2 text-sm font-semibold text-forest-700 hover:text-forest-500 transition-colors">
            ← Tilbage til events
          </Link>

          <div className="rounded-2xl overflow-hidden shadow-lg border border-forest-100">
            <img 
              src={getImageUrl(event.image, 1200) || "https://cdn.brick.site/6866696df3ec584b07678dee/original/image_1c95fe22bd184192b0b2e137f1f67fad"} 
              alt={event.title} 
              className="w-full h-[350px] object-cover" 
            />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            {/* Event Info */}
            <div className="lg:col-span-2 space-y-6">
              <h1 className="text-3xl md:text-4xl font-bold text-forest-950 tracking-tight">{event.title}</h1>
              <div className="text-base text-forest-700 leading-relaxed space-y-4" dangerouslySetInnerHTML={{ __html: event.description }} />
            </div>

            {/* Event Meta Sidebar */}
            <div className="bg-forest-50 p-6 rounded-2xl border border-forest-100 space-y-6 h-fit">
              <h3 className="font-display font-bold text-lg text-forest-950 border-b border-forest-100 pb-2">Detaljer</h3>
              <div className="space-y-4 text-sm text-forest-800">
                <div className="flex gap-3">
                  <Calendar className="w-5 h-5 text-forest-600 shrink-0" />
                  <div>
                    <p className="font-semibold">Dato</p>
                    <p className="text-forest-600">{formatDate(event.date)}</p>
                  </div>
                </div>
                <div className="flex gap-3">
                  <Clock className="w-5 h-5 text-forest-600 shrink-0" />
                  <div>
                    <p className="font-semibold">Tidspunkt</p>
                    <p className="text-forest-600">{event.time}</p>
                  </div>
                </div>
                <div className="flex gap-3">
                  <MapPin className="w-5 h-5 text-forest-600 shrink-0" />
                  <div>
                    <p className="font-semibold">Lokation</p>
                    <p className="text-forest-600">{event.location}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Tilmeldingsformular */}
          {event.status === 'kommende' && (
            <div className="bg-forest-50 p-8 rounded-2xl border border-forest-100 shadow-sm max-w-2xl mx-auto">
              <h2 className="text-2xl font-bold text-forest-950 font-display mb-2 text-center">Tilmeld dig arrangementet</h2>
              <p className="text-sm text-forest-600 text-center mb-8">Udfyld formularen nedenfor for at tilmelde dig og din husstand.</p>

              {formSubmitted ? (
                <div className="bg-emerald-50 border border-emerald-200 text-emerald-900 p-6 rounded-xl text-center space-y-3">
                  <CheckCircle className="w-12 h-12 text-emerald-600 mx-auto" />
                  <h3 className="font-bold text-lg">Tilmeldt med succes!</h3>
                  <p className="text-sm">Tak for din tilmelding. Vi glæder os til at se dig til {event.title}!</p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-5">
                  {/* Honeypot field (ABSOLUTELY HIDDEN) */}
                  <div className="sr-only absolute">
                    <label htmlFor="website_url">Honeypot</label>
                    <input type="text" id="website_url" name="website_url" tabIndex="-1" autoComplete="off" />
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-bold text-forest-800 uppercase tracking-wider mb-1.5" htmlFor="name">Navn *</label>
                      <input required className="w-full px-4 py-3 rounded-xl border border-forest-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-forest-500" type="text" id="name" name="name" placeholder="Dit fulde navn" />
                    </div>
                    <div>
                      <label className="block text-xs font-bold text-forest-800 uppercase tracking-wider mb-1.5" htmlFor="email">E-mail *</label>
                      <input required className="w-full px-4 py-3 rounded-xl border border-forest-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-forest-500" type="email" id="email" name="email" placeholder="din@mail.dk" />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-bold text-forest-800 uppercase tracking-wider mb-1.5" htmlFor="phone">Telefonnummer *</label>
                      <input required className="w-full px-4 py-3 rounded-xl border border-forest-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-forest-500" type="tel" id="phone" name="phone" placeholder="12 34 56 78" />
                    </div>
                    <div>
                      <label className="block text-xs font-bold text-forest-800 uppercase tracking-wider mb-1.5" htmlFor="address">Adresse *</label>
                      <input required className="w-full px-4 py-3 rounded-xl border border-forest-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-forest-500" type="text" id="address" name="address" placeholder="F.eks. Kongevænget 12" />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-bold text-forest-800 uppercase tracking-wider mb-1.5" htmlFor="adults">Antal voksne *</label>
                      <input required min="1" defaultValue="1" className="w-full px-4 py-3 rounded-xl border border-forest-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-forest-500" type="number" id="adults" name="adults" />
                    </div>
                    <div>
                      <label className="block text-xs font-bold text-forest-800 uppercase tracking-wider mb-1.5" htmlFor="children">Antal børn</label>
                      <input min="0" defaultValue="0" className="w-full px-4 py-3 rounded-xl border border-forest-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-forest-500" type="number" id="children" name="children" />
                    </div>
                  </div>

                  {error && (
                    <div className="p-4 bg-red-50 border border-red-200 text-red-800 rounded-xl text-sm">
                      {error}
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={submitting}
                    className="w-full py-3.5 rounded-xl bg-forest-700 hover:bg-forest-600 text-white font-semibold text-sm transition-all shadow-md hover:shadow-forest-700/20 disabled:opacity-50 cursor-pointer"
                  >
                    {submitting ? "Sender tilmelding..." : "Tilmeld mig nu"}
                  </button>
                </form>
              )}
            </div>
          )}
        </div>
      </div>
    </>
  );
}

// --- KONTINGENT PAGE ---
function Kontingent() {
  const settings = useSiteSettings();
  const pageData = usePageContent('kontingent', {
    title: "Kontingent Info",
    subtitle: "Her finder du alt den nødvendige information vedrørende det årlige kontingent for grundejere i Kongeengen.",
    content: "<h2>Hvad går kontingentet til?</h2><p>Grundejerforeningen Kongeengen er en non-profit forening...</p>",
    meta_title: "Kontingent info",
    meta_description: "Få information om kontingent, betalingsfrister og foreningens økonomiske rammer i Grundejerforeningen Kongeengen."
  });

  return (
    <>
      <Meta title={pageData.meta_title || "Kontingent info"} description={pageData.meta_description} />
      <div className="bg-white py-16 md:py-24">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 space-y-10">
          <div className="space-y-4 text-center">
            <h1 className="text-4xl font-bold text-forest-950 tracking-tight">{pageData.title}</h1>
            {pageData.subtitle && (
              <p className="text-lg text-forest-600">{pageData.subtitle}</p>
            )}
          </div>

          <div className="bg-forest-50 p-8 rounded-2xl border border-forest-100 space-y-6">
            <h3 className="font-display font-bold text-xl text-forest-950 border-b border-forest-100 pb-2">Kontingentsats & Betaling</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 text-sm">
              <div>
                <p className="text-xs font-bold text-forest-500 uppercase tracking-wider">Årligt Kontingent</p>
                <p className="text-3xl font-bold text-forest-950 mt-1">{settings.kontingent_amount || "1.800 kr."}</p>
                <p className="text-xs text-forest-600 mt-1">Fastsat på den årlige generalforsamling.</p>
              </div>
              <div>
                <p className="text-xs font-bold text-forest-500 uppercase tracking-wider">Betalingsfrist</p>
                <p className="text-lg font-bold text-forest-950 mt-1">{settings.kontingent_due_date || "1. juni hvert år"}</p>
                <p className="text-xs text-forest-600 mt-1">{settings.kontingent_note || "Opkrævning udsendes via mail/PBS i april."}</p>
              </div>
            </div>
          </div>

          <div 
            className="prose prose-forest text-forest-800 space-y-6"
            dangerouslySetInnerHTML={{ __html: pageData.content }}
          />
        </div>
      </div>
    </>
  );
}

// --- KONTAKT PAGE ---
function Kontakt() {
  const settings = useSiteSettings();
  const pageData = usePageContent('kontakt', {
    title: "Kontakt os",
    subtitle: "Har du spørgsmål, forslag eller ris/ros? Skriv til bestyrelsen via formularen nedenfor.",
    content: "<p>Vi bestræber os på at besvare alle henvendelser inden for 2-3 hverdage.</p>",
    meta_title: "Kontakt",
    meta_description: "Kontakt bestyrelsen i Grundejerforeningen Kongeengen i Vejen. Send os en besked via vores kontaktformular."
  });

  const [formSubmitted, setFormSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);

    const formData = new FormData(e.target);
    const data = Object.fromEntries(formData.entries());

    try {
      const response = await fetch('https://api.dev.auxo.dk/webform/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
      });

      if (response.ok) {
        setFormSubmitted(true);
      } else {
        throw new Error('Der skete en fejl ved indsendelsen. Prøv igen.');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Meta title={pageData.meta_title || "Kontakt"} description={pageData.meta_description} />
      <div className="bg-white py-16 md:py-24">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 space-y-12">
          <div className="space-y-4 text-center">
            <h1 className="text-4xl font-bold text-forest-950 tracking-tight">{pageData.title}</h1>
            {pageData.subtitle && (
              <p className="text-lg text-forest-600 max-w-2xl mx-auto">{pageData.subtitle}</p>
            )}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12 items-start">
            {/* Kontakt Info */}
            <div className="bg-forest-50 p-8 rounded-2xl border border-forest-100 space-y-6">
              <h3 className="font-display font-bold text-xl text-forest-950 border-b border-forest-100 pb-2">Information</h3>
              <div 
                className="text-sm text-forest-700 leading-relaxed"
                dangerouslySetInnerHTML={{ __html: pageData.content }}
              />
              
              <div className="space-y-4 text-sm text-forest-800">
                {settings.email && (
                  <div className="flex items-center gap-3">
                    <Mail className="w-5 h-5 text-forest-600 shrink-0" />
                    <div>
                      <p className="font-semibold">E-mail</p>
                      <p className="text-forest-600 whitespace-nowrap">{settings.email}</p>
                    </div>
                  </div>
                )}
                {settings.phone && (
                  <div className="flex items-center gap-3">
                    <Phone className="w-5 h-5 text-forest-600 shrink-0" />
                    <div>
                      <p className="font-semibold">Telefon</p>
                      <p className="text-forest-600 whitespace-nowrap">{settings.phone}</p>
                    </div>
                  </div>
                )}
                {settings.address && (
                  <div className="flex items-center gap-3">
                    <Map className="w-5 h-5 text-forest-600 shrink-0" />
                    <div>
                      <p className="font-semibold">Adresse</p>
                      <p className="text-forest-600">{settings.address}</p>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Kontaktformular */}
            <div className="lg:col-span-2 bg-white p-8 rounded-2xl border border-forest-100 shadow-sm">
              {formSubmitted ? (
                <div className="bg-emerald-50 border border-emerald-200 text-emerald-900 p-8 rounded-xl text-center space-y-3">
                  <CheckCircle className="w-12 h-12 text-emerald-600 mx-auto" />
                  <h3 className="font-bold text-xl">Besked sendt!</h3>
                  <p className="text-sm">Tak for din henvendelse. Vi vender tilbage til dig så hurtigt som muligt.</p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-5">
                  {/* Honeypot field (ABSOLUTELY HIDDEN) */}
                  <div className="sr-only absolute">
                    <label htmlFor="website_url">Honeypot</label>
                    <input type="text" id="website_url" name="website_url" tabIndex="-1" autoComplete="off" />
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-bold text-forest-800 uppercase tracking-wider mb-1.5" htmlFor="name">Navn *</label>
                      <input required className="w-full px-4 py-3 rounded-xl border border-forest-200 bg-forest-50/50 text-sm focus:outline-none focus:ring-2 focus:ring-forest-500" type="text" id="name" name="name" placeholder="Dit fulde navn" />
                    </div>
                    <div>
                      <label className="block text-xs font-bold text-forest-800 uppercase tracking-wider mb-1.5" htmlFor="email">E-mail *</label>
                      <input required className="w-full px-4 py-3 rounded-xl border border-forest-200 bg-forest-50/50 text-sm focus:outline-none focus:ring-2 focus:ring-forest-500" type="email" id="email" name="email" placeholder="din@mail.dk" />
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-bold text-forest-800 uppercase tracking-wider mb-1.5" htmlFor="phone">Telefonnummer *</label>
                    <input required className="w-full px-4 py-3 rounded-xl border border-forest-200 bg-forest-50/50 text-sm focus:outline-none focus:ring-2 focus:ring-forest-500" type="tel" id="phone" name="phone" placeholder="12 34 56 78" />
                  </div>

                  <div>
                    <label className="block text-xs font-bold text-forest-800 uppercase tracking-wider mb-1.5" htmlFor="message">Besked *</label>
                    <textarea required rows="5" className="w-full px-4 py-3 rounded-xl border border-forest-200 bg-forest-50/50 text-sm focus:outline-none focus:ring-2 focus:ring-forest-500" id="message" name="message" placeholder="Skriv din besked her..."></textarea>
                  </div>

                  {error && (
                    <div className="p-4 bg-red-50 border border-red-200 text-red-800 rounded-xl text-sm">
                      {error}
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={submitting}
                    className="w-full py-3.5 rounded-xl bg-forest-700 hover:bg-forest-600 text-white font-semibold text-sm transition-all shadow-md hover:shadow-forest-700/20 disabled:opacity-50 cursor-pointer"
                  >
                    {submitting ? "Sender besked..." : "Send besked"}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

// --- 404 PAGE ---
function NotFound() {
  return (
    <>
      <Meta title="Siden blev ikke fundet" />
      <div className="bg-white py-24 text-center space-y-6">
        <AlertTriangle className="w-16 h-16 text-forest-600 mx-auto" />
        <h1 className="text-4xl font-bold text-forest-950">404 - Siden blev ikke fundet</h1>
        <p className="text-forest-600 max-w-md mx-auto">Beklager, men den side du leder efter findes ikke eller er blevet flyttet.</p>
        <Link to="/" className="inline-block px-6 py-3 rounded-xl bg-forest-700 hover:bg-forest-600 text-white font-semibold text-sm transition-all">
          Gå til forsiden
        </Link>
      </div>
    </>
  );
}

// --- 403 PAGE ---
function Forbidden() {
  return (
    <>
      <Meta title="Adgang nægtet" />
      <div className="bg-white py-24 text-center space-y-6">
        <Shield className="w-16 h-16 text-red-600 mx-auto" />
        <h1 className="text-4xl font-bold text-forest-950">403 - Adgang nægtet</h1>
        <p className="text-forest-600 max-w-md mx-auto">Du har ikke tilladelse til at tilgå denne side.</p>
        <Link to="/" className="inline-block px-6 py-3 rounded-xl bg-forest-700 hover:bg-forest-600 text-white font-semibold text-sm transition-all">
          Gå til forsiden
        </Link>
      </div>
    </>
  );
}

// --- MAIN APP ---
function App() {
  return (
    <>
      <ScrollToTop />
      <div className="flex flex-col min-h-screen bg-forest-50/30">
        <Navigation />
        <main className="flex-grow">
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/foreningen" element={<Foreningen />} />
            <Route path="/bestyrelsen" element={<Bestyrelsen />} />
            
            {/* Separate Dokumentsider */}
            <Route path="/bestyrelsesmoeder" element={
              <DocumentPageLayout 
                category="bestyrelsesmoede" 
                title="Bestyrelsesmøder" 
                description="Her finder du referater og dagsordener fra bestyrelsens løbende møder."
              />
            } />
            <Route path="/generalforsamling" element={
              <DocumentPageLayout 
                category="generalforsamling" 
                title="Generalforsamling" 
                description="Her finder du indkaldelser, dagsordener og referater fra de årlige generalforsamlinger."
              />
            } />
            <Route path="/oekonomi" element={
              <DocumentPageLayout 
                category="oekonomi" 
                title="Økonomi" 
                description="Her finder du årsregnskaber, budgetter og andre økonomiske dokumenter."
              />
            } />
            <Route path="/vedtaegter" element={
              <DocumentPageLayout 
                category="vedtaegter" 
                title="Vedtægter & Reglementer" 
                description="Her finder du foreningens gældende vedtægter, ordensreglement og deklarationer."
              />
            } />

            <Route path="/kontingent" element={<Kontingent />} />
            <Route path="/events" element={<Events />} />
            <Route path="/events/:id" element={<EventDetail />} />
            <Route path="/kontakt" element={<Kontakt />} />
            <Route path="/nyhedsbrev-modtagere" element={<NewsletterRecipients />} />
            <Route path="/event-tilmeldinger" element={<EventRegistrations />} />
            
            {/* Fejlsider */}
            <Route path="/403" element={<Forbidden />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
        </main>
        <Footer />
      </div>
    </>
  );
}

export default App;
